/** Discovery items that need a product decision, and the grid rows they cover. */

export const PRODUCT_DECISION_SECTIONS = [
  ["items_requiring_human_validation", "Needs human validation"],
  ["other_technology_context", "Other technology context"],
];

export function decisionItemTitle(item) {
  return item?.subject || item?.normalized_name || item?.name || item?.display_name || "Untitled item";
}

export function decisionItemReason(item) {
  return item?.reason_for_ambiguity || item?.summary || item?.why_not_radware || item?.concise_summary || "";
}

export function decisionItems(scope) {
  const items = [];
  for (const [section, label] of PRODUCT_DECISION_SECTIONS) {
    for (const item of scope?.[section] || []) {
      if (item && typeof item === "object") items.push({ ...item, section, sectionLabel: label });
    }
  }
  return items;
}

export function isOpenDecision(item) {
  const decision = item?.product_decision;
  if (!decision?.choice) return true;
  return Boolean(decision.pending);
}

function undecidedGenReq(row, item) {
  if ((row?.technology || "") !== "Gen Req") return false;
  if (row?.scope_decision_item_id) return false;
  if ((row?.label_source || "") === "se") return false;
  return rowOverlapsItem(row, item);
}

export function itemStillNeedsDecision(item, rows) {
  if (!isOpenDecision(item)) return false;
  if (!Array.isArray(rows)) return true;
  return rows.some((row) => undecidedGenReq(row, item));
}

export function countOpenProductRows(scope, rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter((row) => openItemsCoveringRow(row, scope).length).length;
}

export function findDecisionItem(scope, itemId) {
  return decisionItems(scope).find((item) => item.item_id === itemId) || null;
}

function rangesOverlap(loc, rng) {
  if ((loc?.sheet || "") !== (rng?.sheet || "")) return false;
  const rowStart = Number(loc?.row_start);
  const rowEnd = Number(loc?.row_end ?? rowStart);
  const rangeStart = Number(rng?.row_start);
  const rangeEnd = Number(rng?.row_end ?? rangeStart);
  if (![rowStart, rowEnd, rangeStart, rangeEnd].every((value) => Number.isFinite(value))) return false;
  return rowStart <= rangeEnd && rowEnd >= rangeStart;
}

export function rowOverlapsItem(row, item) {
  const ranges = item?.source_ranges || [];
  const loc = row?.source_location;
  if (ranges.length && loc?.sheet) return ranges.some((rng) => rangesOverlap(loc, rng));
  const pages = new Set(
    [...(item?.pages || []), ...(item?.primary_pages || []), ...(item?.supporting_pages || [])]
      .map(Number)
      .filter((page) => Number.isFinite(page))
  );
  if (!pages.size) return false;
  const page = Number(row?.pdf_page ?? row?.source_start);
  return pages.has(page);
}

export function openItemsCoveringRow(row, scope) {
  if ((row?.technology || "") !== "Gen Req") return [];
  if (row?.scope_decision_item_id || (row?.label_source || "") === "se") return [];
  return decisionItems(scope).filter((item) => isOpenDecision(item) && rowOverlapsItem(row, item));
}

export function resolvedDecisionForRow(row, scope) {
  const itemId = row?.scope_decision_item_id;
  if (!itemId) return null;
  const item = findDecisionItem(scope, itemId);
  if (!item?.product_decision?.choice) return null;
  return item;
}

export function decisionLabel(decision, row) {
  if (!decision?.choice) return "";
  const assignment = (decision.row_assignments || []).find((item) => item.row_id === row?.id);
  const technology = assignment?.technology || (row ? row.technology : decision.technology);
  if (technology && technology !== "Gen Req") return `Assigned: ${technology}`;
  if (decision.choice === "mixed" && !row) return "Assigned mixed products";
  if (decision.choice === "gen_req" || technology === "Gen Req" || assignment?.choice === "gen_req") return "Kept as Gen Req";
  if (decision.choice === "product" && decision.technology) return `Assigned: ${decision.technology}`;
  return "Kept as Gen Req";
}

export function decisionStatus(item) {
  const decision = item?.product_decision;
  if (!decision?.choice || decision.pending) {
    const decided = decision?.row_ids?.length || 0;
    if (decision?.pending && decided) {
      return `${decided} decided. Remaining rows still need a product decision.`;
    }
    return "Needs a product decision";
  }
  return decisionLabel(decision);
}
